// -----------------------------------------------------------------------------
// SettingsPage — Apparence, langue, compte, food cost
// StockPro / VisualisPro
// -----------------------------------------------------------------------------

import { T } from "../../i18n/translations";
import { getColors } from "../../styles/theme";
import { Ic, ICONS } from "../Icons";

export default function SettingsPage({
  lang,
  setLang,
  dark,
  setDark,
  accent,
  authUser,
  isStaff,
  foodCostThresholds,
  saveFcThresholds,
  onResetPassword,
}) {
  const t = T[lang];
  const { textPrimary, textSecondary, borderColor } = getColors(dark);

  return (
    <div className="slide-in">
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:22, fontWeight:800, color:textPrimary }}>{t.settings}</h1>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:10, maxWidth:400 }}>

        {/* Apparence */}
        <div className="card" style={{ padding:"18px 20px" }}>
          <div style={{ fontWeight:700, color:textPrimary, marginBottom:14, fontSize:14 }}>Apparence</div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ color:textSecondary, fontSize:13 }}>{t.theme}</span>
            <div style={{ display:"flex", gap:8 }}>
              {[
                { val:true, icon:ICONS.moon, label:t.dark },
                { val:false, icon:ICONS.sun, label:t.light },
              ].map(o => (
                <button
                  key={o.label}
                  onClick={() => setDark(o.val)}
                  style={{ padding:"7px 14px", borderRadius:8, fontSize:12, fontWeight:600, background:dark===o.val?`${accent}20`:dark?"rgba(255,255,255,.04)":"rgba(0,0,0,.04)", color:dark===o.val?accent:textSecondary, border:`1px solid ${dark===o.val?accent+"40":borderColor}`, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}
                >
                  <Ic d={o.icon} size={13} color={dark===o.val?accent:textSecondary} /> {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Langue */}
        <div className="card" style={{ padding:"18px 20px" }}>
          <div style={{ fontWeight:700, color:textPrimary, marginBottom:14, fontSize:14 }}>Langue</div>
          <div style={{ display:"flex", gap:8 }}>
            {[
              { val:"fr", flag:"🇫🇷", label:"Français" },
              { val:"ar", flag:"🇸🇦", label:"عربي" },
            ].map(l => (
              <button
                key={l.val}
                onClick={() => setLang(l.val)}
                style={{ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:600, background:lang===l.val?`${accent}20`:dark?"rgba(255,255,255,.04)":"rgba(0,0,0,.04)", color:lang===l.val?accent:textSecondary, border:`1px solid ${lang===l.val?accent+"40":borderColor}`, cursor:"pointer" }}
              >
                {l.flag} {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Mon compte */}
        <div className="card" style={{ padding:"18px 20px" }}>
          <div style={{ fontWeight:700, color:textPrimary, marginBottom:8, fontSize:14 }}>Mon compte</div>
          <div style={{ color:textSecondary, fontSize:12, marginBottom:4 }}>{authUser.email}</div>
          <div style={{ fontSize:11, color:dark?"#333":"#ccc", fontFamily:"'JetBrains Mono',monospace", marginBottom:14 }}>
            ID: {authUser.uid.substring(0,8)}...
          </div>
          <button
            onClick={onResetPassword}
            style={{ width:"100%", padding:"10px", borderRadius:9, background:`${accent}15`, color:accent, fontWeight:600, fontSize:13, border:`1px solid ${accent}30`, cursor:"pointer" }}
          >
            🔑 Changer mon mot de passe
          </button>
        </div>

        {/* Food Cost — admin only */}
        {!isStaff && (
          <div className="card" style={{ padding:"18px 20px" }}>
            <div style={{ fontWeight:700, color:textPrimary, marginBottom:4, fontSize:14 }}>{t.foodCostTitle}</div>
            <p style={{ fontSize:12, color:textSecondary, marginBottom:14 }}>Personnalisez les seuils d'alerte food cost (en %)</p>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={{ display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:13, color:textPrimary, marginBottom:6 }}>
                  <span style={{ display:"flex", alignItems:"center", gap:6 }}>🟢 {t.foodCostGreen}</span>
                  <span style={{ fontWeight:700, color:"#10b981", fontFamily:"'JetBrains Mono',monospace" }}>{foodCostThresholds.green}%</span>
                </label>
                <input
                  type="range"
                  min="10"
                  max="50"
                  step="1"
                  value={foodCostThresholds.green}
                  onChange={(e) => saveFcThresholds({ ...foodCostThresholds, green:parseInt(e.target.value) })}
                  style={{ width:"100%", accentColor:"#10b981" }}
                />
              </div>
              <div>
                <label style={{ display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:13, color:textPrimary, marginBottom:6 }}>
                  <span style={{ display:"flex", alignItems:"center", gap:6 }}>🟡 {t.foodCostOrange}</span>
                  <span style={{ fontWeight:700, color:"#f59e0b", fontFamily:"'JetBrains Mono',monospace" }}>{foodCostThresholds.orange}%</span>
                </label>
                <input
                  type="range"
                  min="10"
                  max="60"
                  step="1"
                  value={foodCostThresholds.orange}
                  onChange={(e) => saveFcThresholds({ ...foodCostThresholds, orange:parseInt(e.target.value) })}
                  style={{ width:"100%", accentColor:"#f59e0b" }}
                />
              </div>
              <div style={{ padding:"8px 12px", borderRadius:8, background:dark?"rgba(255,255,255,.03)":"rgba(0,0,0,.03)", fontSize:11, color:textSecondary }}>
                🟢 &lt; {foodCostThresholds.green}% · 🟡 {foodCostThresholds.green}-{foodCostThresholds.orange}% · 🔴 &gt; {foodCostThresholds.orange}%
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
